// The Objectives & Tasks list: objectives as collapsible groups of task rows with inline edits,
// the task drawer, safe delete with undo, template import, and bulk edits. Used by a client's
// Objectives & Tasks tab and (clientId = null) by the Sawtooth Internal page.
import React, { useState, useEffect, useRef } from "react";
import UserAvatar from "../crm/UserAvatar.jsx";
import { colors, font, displayName } from "../utils/theme.js";
import { api } from "../api/client.js";
import TaskDetail from "./TaskDetail.jsx";
import ImportTemplate from "../templates/ImportTemplate.jsx";
import { projectTitle, splitObjectiveTasks } from "./clientLogic.js";
import ConfirmDialog from "./ConfirmDialog.jsx";
import BulkBar from "./BulkBar.jsx";
import { bulkFields, runLimited } from "./bulkTasks.js";
import { AssigneeSelect, DateChip, PrivateMark, EyeIcon } from "./InlineTaskFields.jsx";
import { section, inputStyle, primaryBtn, ghostBtn, STATUS_STYLE, STATUSES } from "./ui.js";

// Column slots for a task row's inline fields (widths fit "Start 29 September" / "Not Started").
const taskCol = {
  assignee: { width: 150, flexShrink: 0, display: "flex" },
  date: { width: 140, flexShrink: 0, display: "flex" },
  status: { width: 112, flexShrink: 0, display: "flex" },
};

const displayNameOf = (users, id) => { const u = (users || []).find((x) => x.id === id || x.email === id); return u ? displayName(u) : id; };

// ── Objectives & Tasks tab ──
const UNDO_MS = 10000; // how long a delete can be undone before it is persisted
// clientId = null → internal work: no Private controls, no "Client" assignee. looseTasks (an array)
// adds a final "No objective" group; initialObjectiveId opens and scrolls to that objective.
const LOOSE = { id: "__no_objective__", name: "No objective", _loose: true };
export default function WorkList({ clientId, current, work, initialTaskId, initialObjectiveId = null, users, objectives, tasks, setObjectives, setTasks, clientName, onChanged, flash, reload, looseTasks = null }) {
  const [newObj, setNewObj] = useState("");
  const [openTask, setOpenTask] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const [showCompleted, setShowCompleted] = useState({}); // objective id → completed tasks expanded
  const [showImport, setShowImport] = useState(false);
  const [renaming, setRenaming] = useState(null); // { id, value } while an objective name is being edited
  const openedLinkRef = useRef(false);
  const scrolledRef = useRef(false);
  useEffect(() => {
    if (!initialObjectiveId || scrolledRef.current) return;
    const el = document.getElementById(`objective-${initialObjectiveId}`);
    if (el) { scrolledRef.current = true; el.scrollIntoView?.({ block: "start" }); }
  });
  // Bulk selection (spec §B): task ids; ids of tasks that no longer exist are pruned.
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const toggleSelected = (ids, on) => setSelected((s) => {
    const n = new Set(s);
    for (const id of ids) { if (on) n.add(id); else n.delete(id); }
    return n;
  });
  useEffect(() => {
    if (!initialTaskId || openedLinkRef.current) return;
    const t = tasks.find((x) => x.id === initialTaskId);
    if (t) { openedLinkRef.current = true; setOpenTask(t); }
  }, [initialTaskId, tasks]);
  const currentId = current?.id || null;

  const addObjective = async () => {
    const name = newObj.trim(); if (!name) return;
    setNewObj("");
    const tempId = "temp-" + Date.now();
    setObjectives((o) => [...o, { id: tempId, name, clientId, projectId: currentId, status: "Not Started", _pending: true }]);
    const res = await api.objectives.create({ name, clientId, projectId: currentId });
    if (res.ok) { setObjectives((o) => o.map((x) => (x.id === tempId ? res.data : x))); onChanged?.(); }
    else { setObjectives((o) => o.filter((x) => x.id !== tempId)); flash("error", "Couldn’t add objective."); }
  };
  const addTask = async (objectiveId, name) => {
    if (!name.trim()) return;
    const tempId = "temp-" + Date.now();
    // A task joins its objective's project (an objective shown under another project keeps its
    // tasks there); default the assignee to that project's PM (changeable in the task panel).
    const otherGroup = work.others.find((g) => g.objectives.some((o) => o.id === objectiveId));
    const project = otherGroup ? otherGroup.project : current;
    const projectId = project?.id || null;
    const pm = project?.projectManager;
    const assignee = pm ? { type: "user", userId: pm } : { type: "unassigned" };
    setTasks((t) => [...t, { id: tempId, name: name.trim(), clientId, projectId, objectiveId, assignee, status: "Not Started", history: [], dueDate: null, _pending: true }]);
    const res = await api.tasks.create({ name: name.trim(), clientId, projectId, objectiveId, assignee });
    if (res.ok) { setTasks((t) => t.map((x) => (x.id === tempId ? res.data : x))); onChanged?.(); }
    else { setTasks((t) => t.filter((x) => x.id !== tempId)); flash("error", "Couldn’t add task."); }
  };
  const saveTask = async (id, fields) => {
    const prev = tasks;
    setTasks((t) => t.map((x) => (x.id === id ? { ...x, ...fields } : x)));
    const res = await api.tasks.update(id, fields);
    if (res.ok) { setTasks((t) => t.map((x) => (x.id === id ? res.data : x))); onChanged?.(); return true; }
    setTasks(prev); flash("error", "Couldn’t save task — reverted."); return false;
  };
  const saveObjective = async (id, fields) => {
    const prev = objectives;
    setObjectives((o) => o.map((x) => (x.id === id ? { ...x, ...fields } : x)));
    const res = await api.objectives.update(id, fields);
    if (res.ok) { setObjectives((o) => o.map((x) => (x.id === id ? res.data : x))); onChanged?.(); }
    else { setObjectives(prev); flash("error", "Couldn’t update objective — reverted."); }
  };
  // Rename commits a trimmed, changed, non-blank name; anything else just closes the editor.
  const commitRename = (o) => {
    const name = (renaming?.value || "").trim();
    setRenaming(null);
    if (name && name !== o.name) saveObjective(o.id, { name });
  };
  // ── Safe delete: confirm in-app, hide immediately, persist only after an undo window. ──
  // One delete is pending at a time; starting another (or leaving the tab/page) commits it.
  const [confirming, setConfirming] = useState(null); // { kind: "task"|"objective", item }
  const [undoItem, setUndoItem] = useState(null);      // { label } while a delete can be undone
  const pendingRef = useRef(null);                      // { timer, commit, restore }
  const flushPending = () => {
    const p = pendingRef.current; if (!p) return;
    clearTimeout(p.timer); pendingRef.current = null; setUndoItem(null); p.commit();
  };
  useEffect(() => () => flushPending(), []); // eslint-disable-line react-hooks/exhaustive-deps
  const scheduleDelete = ({ label, hide, restore, commit }) => {
    flushPending();
    hide();
    const timer = setTimeout(() => { pendingRef.current = null; setUndoItem(null); commit(); }, UNDO_MS);
    pendingRef.current = { timer, commit, restore };
    setUndoItem({ label });
  };
  const undoDelete = () => {
    const p = pendingRef.current; if (!p) return;
    clearTimeout(p.timer); pendingRef.current = null; setUndoItem(null); p.restore();
  };

  const deleteTask = (t) => {
    scheduleDelete({
      label: `Task “${t.name}” deleted`,
      hide: () => setTasks((ts) => ts.filter((x) => x.id !== t.id)),
      restore: () => setTasks((ts) => (ts.some((x) => x.id === t.id) ? ts : [...ts, t])),
      commit: async () => {
        const r = await api.tasks.remove(t.id);
        if (r.ok) { setTasks((ts) => ts.filter((x) => x.id !== t.id)); onChanged?.(); }
        else { setTasks((ts) => (ts.some((x) => x.id === t.id) ? ts : [...ts, t])); flash("error", "Couldn’t delete task — restored."); }
      },
    });
  };
  const deleteObjective = (o) => {
    const its = tasks.filter((t) => t.objectiveId === o.id);
    const idx = objectives.findIndex((x) => x.id === o.id);
    scheduleDelete({
      label: `Objective “${o.name}” deleted`,
      hide: () => { setObjectives((os) => os.filter((x) => x.id !== o.id)); setTasks((ts) => ts.filter((x) => x.objectiveId !== o.id)); },
      restore: () => {
        setObjectives((os) => { if (os.some((x) => x.id === o.id)) return os; const n = [...os]; n.splice(Math.max(0, Math.min(idx, n.length)), 0, o); return n; });
        setTasks((ts) => [...ts, ...its.filter((t) => !ts.some((x) => x.id === t.id))]);
      },
      commit: async () => {
        try {
          for (const t of its) { const r = await api.tasks.remove(t.id); if (!r.ok) throw new Error("task delete failed"); }
          const res = await api.objectives.remove(o.id);
          if (!res.ok) throw new Error("objective delete failed");
          onChanged?.();
        } catch {
          // Some tasks may already be gone server-side — reload the truth rather than guess.
          flash("error", "Couldn’t fully delete objective — reloaded.");
          reload();
        }
      },
    });
  };
  const deleteTasks = (items) => {
    const ids = new Set(items.map((t) => t.id));
    const putBack = (back) => setTasks((ts) => [...ts, ...back.filter((t) => !ts.some((x) => x.id === t.id))]);
    scheduleDelete({
      label: `${items.length} task${items.length !== 1 ? "s" : ""} deleted`,
      hide: () => setTasks((ts) => ts.filter((x) => !ids.has(x.id))),
      restore: () => putBack(items),
      commit: async () => {
        const { ok, failed } = await runLimited(items, (t) => api.tasks.remove(t.id));
        if (ok.length) {
          const gone = new Set(ok.map(({ item }) => item.id));
          setTasks((ts) => ts.filter((x) => !gone.has(x.id))); // a reload during the undo window may have brought them back
          onChanged?.();
        }
        if (failed.length) {
          putBack(failed.map((f) => f.item));
          flash("error", `Couldn’t delete ${failed.length} task${failed.length !== 1 ? "s" : ""} — restored.`);
        }
      },
    });
  };
  const confirmDelete = () => {
    const c = confirming; setConfirming(null);
    if (c.kind === "task") deleteTask(c.item);
    else if (c.kind === "tasks") { setSelected(new Set()); deleteTasks(c.items); }
    else deleteObjective(c.item);
  };

  // Apply one bulk action to every selected task: optimistic, then reconcile per task —
  // successes take the server's copy, failures revert and stay selected.
  const applyBulk = async (requested) => {
    const action = requested.kind === "move" && requested.objectiveId === LOOSE.id ? { ...requested, objectiveId: null, projectId: null } : requested;
    const items = selectedTasks;
    if (!items.length) return;
    const snap = new Map(items.map((t) => [t.id, t]));
    setBulkBusy(true);
    setTasks((ts) => ts.map((t) => (snap.has(t.id) ? { ...t, ...bulkFields(snap.get(t.id), action) } : t)));
    const { ok, failed } = await runLimited(items, (t) => api.tasks.update(t.id, bulkFields(t, action)));
    const saved = new Map(ok.map(({ item, result }) => [item.id, result?.data]));
    const failedIds = new Set(failed.map((f) => f.item.id));
    setTasks((ts) => ts.map((t) => (saved.has(t.id) ? (saved.get(t.id) || t) : failedIds.has(t.id) ? snap.get(t.id) : t)));
    setBulkBusy(false);
    if (ok.length) onChanged?.();
    if (!failed.length) {
      setSelected(new Set());
      flash("success", `Updated ${ok.length} task${ok.length !== 1 ? "s" : ""}`);
    } else {
      setSelected(failedIds);
      flash("error", `${ok.length} updated, ${failed.length} failed — still selected`);
    }
  };

  // Current project's objectives open at the top; each other live project's below under its
  // name; objectives tied to archived projects last. Unlinked work sits with the primary project.
  const currentObjs = work.current.objectives;
  const earlierObjs = work.earlier;

  // Move targets: saved objectives only (a pending one has a temp id). Current and other-live
  // groups follow addTask (the group's project); earlier objectives keep their own project.
  const target = (o, projectId, group) => ({ id: o.id, name: o.name, projectId, group });
  const moveTargets = [
    ...currentObjs.map((o) => target(o, current?.id || null, "")),
    ...work.others.flatMap(({ project, objectives: objs }) => objs.map((o) => target(o, project.id, projectTitle(project)))),
    ...earlierObjs.map((o) => target(o, o.projectId || null, "Earlier / other engagements")),
    // Internal page: a task can also go back to "No objective" (mapped to null in applyBulk).
    ...(looseTasks ? [target(LOOSE, null, "")] : []),
  ].filter((t) => !objectives.find((o) => o.id === t.id)?._pending);

  // Only tasks the user can see stay selected: expanded objectives' open tasks, plus completed
  // ones where that list is shown. Hidden (collapsed, completed, deleted) tasks drop out.
  const visibleIds = new Set();
  const tasksOf = (o) => (o._loose ? looseTasks || [] : tasks.filter((t) => t.objectiveId === o.id));
  const isObjOpen = (o, expandedDefault) => (collapsed[o.id] === undefined ? expandedDefault || o.id === initialObjectiveId : !collapsed[o.id]);
  const addVisible = (o, expandedDefault) => {
    if (!isObjOpen(o, expandedDefault)) return;
    const { open, completed } = splitObjectiveTasks(tasksOf(o));
    for (const t of [...open, ...(showCompleted[o.id] ? completed : [])]) if (!t._pending) visibleIds.add(t.id);
  };
  currentObjs.forEach((o) => addVisible(o, true));
  work.others.forEach(({ objectives: objs }) => objs.forEach((o) => addVisible(o, false)));
  earlierObjs.forEach((o) => addVisible(o, false));
  if (looseTasks) addVisible(LOOSE, true);
  const visibleKey = [...visibleIds].sort().join("|");
  useEffect(() => {
    setSelected((s) => {
      if (!s.size) return s;
      const kept = [...s].filter((id) => visibleIds.has(id));
      return kept.length === s.size ? s : new Set(kept);
    });
  }, [visibleKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const selectedTasks = tasks.filter((t) => selected.has(t.id) && visibleIds.has(t.id));

  const renderObj = (o, expandedDefault) => {
    const its = tasksOf(o);
    const isOpen = isObjOpen(o, expandedDefault);
    // Open tasks are ordered by due date, so the next one up is simply first — no highlight.
    const { open, completed } = splitObjectiveTasks(its);
    const done = completed.length;
    const pct = its.length ? Math.round((done / its.length) * 100) : 0;
    const today = new Date().toISOString().slice(0, 10);
    const taskRow = (t) => {
      const isDone = t.status === "Completed";
      const overdue = !isDone && t.dueDate && t.dueDate.slice(0, 10) < today;
      return (
        <div key={t.id} onClick={() => setOpenTask(t)}
          style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 7, cursor: "pointer", opacity: t._pending ? 0.6 : 1,
            background: "transparent", border: "1px solid transparent" }}
          onMouseEnter={(e) => { e.currentTarget.style.background = colors.surfaceAlt; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
          <input type="checkbox" aria-label={`Select ${t.name}`} checked={selected.has(t.id)} disabled={!!t._pending}
            onClick={(e) => e.stopPropagation()} onChange={(e) => toggleSelected([t.id], e.target.checked)}
            style={{ margin: 0, cursor: t._pending ? "default" : "pointer", flexShrink: 0 }} />
          <span style={{ flex: 1, minWidth: 80, fontSize: 13, color: isDone ? colors.textDim : colors.text, fontWeight: 400, textDecoration: isDone ? "line-through" : "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.name}</span>
          {clientId && (t.private || o.private) && <PrivateMark objectiveLevel={!t.private} />}
          {/* Fixed-width columns so assignee/start/due/status line up from row to row. */}
          {t._pending ? (
            <>
              <span style={taskCol.assignee}><AssigneeChip assignee={t.assignee} users={users} /></span>
              <span style={taskCol.date} /><span style={taskCol.date} />
            </>
          ) : (
            <>
              <span style={taskCol.assignee}><AssigneeSelect assignee={t.assignee} users={users} clientId={clientId} onChange={(a) => saveTask(t.id, { assignee: a })} /></span>
              <span style={taskCol.date}><DateChip label="Start" value={t.startDate} onChange={(d) => saveTask(t.id, { startDate: d })} /></span>
              <span style={taskCol.date}><DateChip label="Due" value={t.dueDate} danger={overdue} onChange={(d) => saveTask(t.id, { dueDate: d })} /></span>
            </>
          )}
          <span style={taskCol.status}><StatusSelect value={t.status} onChange={(s) => saveTask(t.id, { status: s })} small /></span>
          <button onClick={(e) => { e.stopPropagation(); setConfirming({ kind: "task", item: t }); }} title="Delete task" aria-label={`Delete task ${t.name}`}
            disabled={t._pending}
            style={{ background: "transparent", border: "none", cursor: "pointer", color: colors.textDim, fontSize: 13, lineHeight: 1, padding: "2px 4px", fontFamily: font }}
            onMouseEnter={(e) => (e.currentTarget.style.color = colors.danger)}
            onMouseLeave={(e) => (e.currentTarget.style.color = colors.textDim)}>✕</button>
        </div>
      );
    };
    const showDone = !!showCompleted[o.id];
    // Select-all covers what's visible: open tasks, plus completed ones only when expanded.
    const visibleIds = [...open, ...(showDone ? completed : [])].filter((t) => !t._pending).map((t) => t.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
    return (
      <div key={o.id} id={`objective-${o.id}`} style={{ border: `1px solid ${colors.border}`, borderRadius: 8, marginBottom: 8 }}>
        <div onClick={() => setCollapsed((c) => ({ ...c, [o.id]: isOpen }))} style={{ display: "flex", alignItems: "center", gap: 9, padding: "10px 12px", cursor: "pointer" }}>
          <span style={{ transform: isOpen ? "rotate(90deg)" : "none", transition: "transform .15s", color: colors.textDim }}>▶</span>
          <input type="checkbox" aria-label={`Select all in ${o.name}`} checked={allSelected} disabled={!visibleIds.length}
            onClick={(e) => e.stopPropagation()} onChange={(e) => toggleSelected(visibleIds, e.target.checked)}
            style={{ margin: 0, cursor: visibleIds.length ? "pointer" : "default", flexShrink: 0 }} />
          {renaming?.id === o.id ? (
            <input autoFocus aria-label="Objective name" value={renaming.value}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => setRenaming({ id: o.id, value: e.target.value })}
              onKeyDown={(e) => { if (e.key === "Enter") commitRename(o); else if (e.key === "Escape") setRenaming(null); }}
              onBlur={() => commitRename(o)}
              style={{ ...inputStyle, flex: 1, fontWeight: 700, fontSize: 14, padding: "4px 8px" }} />
          ) : (
            <span style={{ fontWeight: 700, color: colors.text, fontSize: 14, flex: 1 }}>{o.name}</span>
          )}
          {its.length > 0 && (
            <span style={{ width: 80, height: 5, background: colors.surfaceAlt, borderRadius: 3, overflow: "hidden", flexShrink: 0 }}>
              <span style={{ display: "block", width: `${pct}%`, height: 5, background: colors.success }} />
            </span>
          )}
          <span style={{ fontSize: 11, color: colors.textDim, fontVariantNumeric: "tabular-nums" }}>{done} / {its.length} done</span>
          {!o._loose && <>
          <StatusSelect value={o.status || "Not Started"} onChange={(s) => saveObjective(o.id, { status: s })} />
          <button
            onClick={(e) => { e.stopPropagation(); setRenaming({ id: o.id, value: o.name }); }}
            title="Rename objective" aria-label="Rename objective"
            style={{ background: "transparent", border: "none", cursor: "pointer", color: colors.textDim, fontSize: 14, lineHeight: 1, padding: "2px 4px", fontFamily: font }}
            onMouseEnter={(e) => (e.currentTarget.style.color = colors.text)}
            onMouseLeave={(e) => (e.currentTarget.style.color = colors.textDim)}
          >✎</button>
          {clientId && <button
            onClick={(e) => { e.stopPropagation(); saveObjective(o.id, { private: !o.private }); }}
            title={o.private ? "Make objective visible to client" : "Make objective private"}
            aria-label={o.private ? "Make objective visible to client" : "Make objective private"}
            style={{ background: "transparent", border: "none", cursor: "pointer", lineHeight: 1, padding: "2px 4px", fontFamily: font, color: o.private ? colors.text : colors.textDim, opacity: o.private ? 1 : 0.6 }}
          ><EyeIcon off={!!o.private} size={14} /></button>}
          <button
            onClick={(e) => { e.stopPropagation(); setConfirming({ kind: "objective", item: o }); }}
            title="Delete objective"
            style={{ background: "transparent", border: "none", cursor: "pointer", color: colors.textDim, fontSize: 15, lineHeight: 1, padding: "2px 4px", fontFamily: font }}
            onMouseEnter={(e) => (e.currentTarget.style.color = colors.danger)}
            onMouseLeave={(e) => (e.currentTarget.style.color = colors.textDim)}
          >✕</button>
          </>}
        </div>
        {isOpen && (
          <div style={{ padding: "0 12px 12px 32px" }}>
            {open.map(taskRow)}
            {open.length === 0 && its.length > 0 && <div style={{ fontSize: 12, color: colors.textDim, padding: "8px 10px" }}>All tasks complete.</div>}
            {completed.length > 0 && (
              <button onClick={() => setShowCompleted((s) => ({ ...s, [o.id]: !showDone }))}
                style={{ marginTop: 6, width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 7, border: `1px dashed ${colors.borderLight}`, background: "transparent", color: colors.textDim, fontFamily: font, fontSize: 12, cursor: "pointer", textAlign: "left" }}>
                <span style={{ color: colors.success, fontWeight: 700 }}>✓</span>
                <span style={{ flex: 1 }}>{completed.length} completed task{completed.length !== 1 ? "s" : ""}</span>
                <span style={{ color: colors.accent }}>{showDone ? "Hide" : "Show"}</span>
              </button>
            )}
            {showDone && completed.map(taskRow)}
            <AddInline placeholder="+ Add task" onAdd={(v) => addTask(o._loose ? null : o.id, v)} />
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={section}>
      <BulkBar count={selectedTasks.length} users={users} clientId={clientId} objectives={moveTargets} busy={bulkBusy}
        onApply={applyBulk} onDelete={() => setConfirming({ kind: "tasks", items: selectedTasks })} onClear={() => setSelected(new Set())} />
      {objectives.length === 0 && <div style={{ color: colors.textDim, fontSize: 13, marginBottom: 10 }}>No objectives yet — add the first below.</div>}
      {currentObjs.map((o) => renderObj(o, true))}
      {work.others.map(({ project, objectives: objs }) => (
        <React.Fragment key={project.id}>
          <div style={{ fontSize: 11, color: colors.textDim, margin: "14px 0 6px", textTransform: "uppercase", letterSpacing: 1 }}>{projectTitle(project)}{project.projectManager ? ` · PM ${displayNameOf(users, project.projectManager)}` : ""}</div>
          {objs.map((o) => renderObj(o, false))}
        </React.Fragment>
      ))}
      {earlierObjs.length > 0 && <div style={{ fontSize: 11, color: colors.textDim, margin: "14px 0 6px", textTransform: "uppercase", letterSpacing: 1 }}>Earlier / other engagements</div>}
      {earlierObjs.map((o) => renderObj(o, false))}
      {looseTasks && renderObj(LOOSE, true)}
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <input style={inputStyle} value={newObj} onChange={(e) => setNewObj(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addObjective()} placeholder="New objective name" />
        <button onClick={addObjective} style={primaryBtn}>Add objective</button>
        <button onClick={() => setShowImport(true)} style={ghostBtn}>Import template</button>
      </div>
      {showImport && (
        <ImportTemplate clientId={clientId} projectId={current?.id || null}
          onClose={() => setShowImport(false)} onImported={() => { setShowImport(false); reload(); onChanged?.(); }} />
      )}
      {openTask && (
        <TaskDetail task={openTask} users={users} clientId={clientId} clientName={clientName}
          onSave={(fields) => saveTask(openTask.id, fields)} onClose={() => setOpenTask(null)}
          onDelete={openTask._pending ? undefined : () => { setConfirming({ kind: "task", item: openTask }); setOpenTask(null); }} />
      )}
      {confirming?.kind === "tasks" && (
        <ConfirmDialog title={`Delete ${confirming.items.length} task${confirming.items.length !== 1 ? "s" : ""}?`}
          confirmLabel={`Delete ${confirming.items.length} task${confirming.items.length !== 1 ? "s" : ""}`}
          onCancel={() => setConfirming(null)} onConfirm={confirmDelete}>
          The selected tasks will be deleted. You’ll have {UNDO_MS / 1000} seconds to undo.
        </ConfirmDialog>
      )}
      {confirming && confirming.kind !== "tasks" && (() => {
        const { kind, item } = confirming;
        const n = kind === "objective" ? tasks.filter((t) => t.objectiveId === item.id).length : 0;
        return (
          <ConfirmDialog title={kind === "task" ? "Delete task?" : "Delete objective?"}
            confirmLabel={kind === "task" ? "Delete task" : n ? `Delete objective + ${n} task${n !== 1 ? "s" : ""}` : "Delete objective"}
            onCancel={() => setConfirming(null)} onConfirm={confirmDelete}>
            {kind === "task"
              ? <>“<b style={{ color: colors.text }}>{item.name}</b>” will be deleted.</>
              : <>“<b style={{ color: colors.text }}>{item.name}</b>”{n ? <> and its <b style={{ color: colors.text }}>{n} task{n !== 1 ? "s" : ""}</b></> : null} will be deleted.</>}
            {" "}You’ll have {UNDO_MS / 1000} seconds to undo.
          </ConfirmDialog>
        );
      })()}
      {undoItem && (
        <div role="status" style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", zIndex: 1250, display: "flex", alignItems: "center", gap: 14, padding: "10px 14px 10px 16px", borderRadius: 10, background: colors.surfaceAlt, border: `1px solid ${colors.borderLight}`, boxShadow: "0 8px 24px rgba(0,0,0,0.45)", fontSize: 13, color: colors.text, maxWidth: "90vw" }}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{undoItem.label}</span>
          <button onClick={undoDelete} style={{ ...primaryBtn, padding: "5px 12px" }}>Undo</button>
        </div>
      )}
    </div>
  );
}
function AddInline({ placeholder, onAdd }) {
  const [v, setV] = useState("");
  return <input style={{ ...inputStyle, marginTop: 6 }} value={v} placeholder={placeholder}
    onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { onAdd(v); setV(""); } }} />;
}

// Assignee on a task row: a staff member (photo or initials + name), the client, or unassigned.
function AssigneeChip({ assignee, users }) {
  if (!assignee || assignee.type === "unassigned") return <span style={{ fontSize: 11, color: colors.textDim, whiteSpace: "nowrap" }}>Unassigned</span>;
  if (assignee.type === "client") return <span style={{ fontSize: 10, fontWeight: 700, color: colors.warning, letterSpacing: 0.4 }}>CLIENT</span>;
  const u = (users || []).find((x) => x.id === assignee.userId || x.email === assignee.userId);
  return <UserAvatar user={u} showName label={u ? undefined : assignee.userId} />;
}

// Inline status pill-dropdown for quick updates in a row (stops the row's click handler).
function StatusSelect({ value, onChange, small }) {
  const st = STATUS_STYLE[value] || STATUS_STYLE["Not Started"];
  return (
    <select value={value} onClick={(e) => e.stopPropagation()} onChange={(e) => { e.stopPropagation(); onChange(e.target.value); }}
      style={{ fontSize: small ? 10.5 : 12, fontWeight: 700, color: st.color, background: st.bg, border: `1px solid ${colors.border}`, borderRadius: 20, padding: small ? "3px 8px" : "4px 10px", fontFamily: font, cursor: "pointer", outline: "none" }}>
      {STATUSES.map((s) => <option key={s} value={s} style={{ color: colors.text, background: colors.surface }}>{s}</option>)}
    </select>
  );
}
